import TimePicker12 from './TimePicker12';
import { useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { submitDemoRequest, VERTICAL } from '../api';

// v3_fix_issues #10 & #11 - "Request a Demo" from the footer. A prospective business owner picks a
// convenient time; the website owner + the appointed marketing manager are notified and follow
// up to onboard them. No calendar/availability system - preferred date/time is a hint, marketing
// confirms a real slot directly.
const empty = { name: '', email: '', phone: '', preferredDate: '', preferredTime: '', businessType: '', message: '' };

export default function RequestDemoModal({ show, onHide }) {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState('');

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const close = () => {
    onHide();
    // reset after the modal has animated out
    setTimeout(() => { setForm(empty); setErrors({}); setDone(false); setServerError(''); }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = 'Please tell us your name';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Enter a valid email';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    setServerError('');
    try {
      await submitDemoRequest({
        vertical: VERTICAL,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        preferredDate: form.preferredDate || undefined,
        preferredTime: form.preferredTime || undefined,
        businessType: form.businessType.trim() || undefined,
        message: form.message.trim() || undefined
      });
      setDone(true);
    } catch (err) {
      setServerError(err.response?.data?.msg || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={close} centered>
      <Modal.Header closeButton>
        <Modal.Title>Request a demo</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {done ? (
          <Alert variant="success" className="mb-0">
            Thanks! Our team will reach out to schedule your demo and help you start your business on the platform.
          </Alert>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            {serverError && <Alert variant="danger">{serverError}</Alert>}
            <Row className="g-2">
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-0">Your name *</Form.Label>
                  <Form.Control size="sm" aria-label="Your name" value={form.name} onChange={set('name')} isInvalid={!!errors.name} />
                  <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-0">Email *</Form.Label>
                  <Form.Control size="sm" type="email" aria-label="Email" value={form.email} onChange={set('email')} isInvalid={!!errors.email} />
                  <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-0">Phone</Form.Label>
                  <Form.Control size="sm" aria-label="Phone" value={form.phone} onChange={set('phone')} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-0">Business type</Form.Label>
                  <Form.Control size="sm" aria-label="Business type" placeholder="e.g. car rental fleet" value={form.businessType} onChange={set('businessType')} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-0">Preferred date</Form.Label>
                  <Form.Control size="sm" type="date" aria-label="Preferred date" value={form.preferredDate} onChange={set('preferredDate')} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-0">Preferred time</Form.Label>
                  {/* A distinct aria-label from the visible Form.Label above, not "Preferred time"
                      verbatim - the two would otherwise share the exact same accessible text,
                      making it ambiguous which one a screen reader user or an automated query is
                      referring to (this control isn't htmlFor-linked to the label since it's a
                      composite of 3 selects, not one field with an id). */}
                  <TimePicker12 size="sm" aria-label="Preferred time slot" value={form.preferredTime} onChange={set('preferredTime')} />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="small mb-0">Anything you'd like us to know?</Form.Label>
                  <Form.Control size="sm" as="textarea" rows={2} aria-label="Message" value={form.message} onChange={set('message')} />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end gap-2">
              <Button size="sm" variant="outline-secondary" onClick={close}>Cancel</Button>
              <Button size="sm" type="submit" disabled={submitting}>
                {submitting ? 'Sending…' : 'Request demo'}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
}
